import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { useToaster } from '@/hooks/useToaster';
import { useUserScope } from '@/hooks/useUserScope';
import {
  getMessageRelayTargetsV2QueryOptions,
  type MessageRelayTargetV2,
} from '@/services/getMessageRelayTargetsV2';
import { relayMessageV2 } from '@/services/relayMessageV2';

const NO_TARGETS: MessageRelayTargetV2[] = [];

/** How a relay target reads in the long-press menu and the confirmation toast. */
export function getRelayTargetLabel(target: MessageRelayTargetV2): string {
  if (target.audience === 'submitter') {
    return 'submitter';
  }

  return target.expert?.name || 'expert';
}

/**
 * Relaying one message between the two sides of an Ask Anonymously case.
 *
 * The server decides which threads are on the other side and copies the
 * message's text, files and references there, so this only lists the targets
 * and sends the choice. Only staff acting as Ask Anonymously get targets; for
 * everyone else the list is empty and long press stays a plain copy.
 *
 * A relay posts to a real person, so the outcome is always confirmed by toast,
 * including a relay the server had already made, which it answers with the
 * earlier copy instead of posting twice.
 *
 * The server's duplicate check reads before it posts, so two requests for the
 * same message and thread sent together could both get through. Picking Relay
 * again before the first request has answered is the way that happens, so a
 * relay already in flight for that pair is ignored here.
 */
export function useMessageRelay({
  conversationId,
  enabled,
}: {
  conversationId: string;
  /** Primum staff on an Ask Anonymously thread. */
  enabled: boolean;
}) {
  const { userId } = useUserScope();

  const toast = useToaster();

  const { track } = useChatAnalytics();

  const { data } = useQuery(
    getMessageRelayTargetsV2QueryOptions(userId, { conversationId }, enabled)
  );

  const relayTargets = enabled ? (data?.targets ?? NO_TARGETS) : NO_TARGETS;

  /**
   * `mutateAsync`, not `mutate` with per-call callbacks: React Query runs those
   * callbacks for the latest call only, so a relay overtaken by another one
   * would lose its toast and never clear its in-flight key, and every later
   * relay of that message there would be silently ignored.
   */
  const { mutateAsync } = useMutation({ mutationFn: relayMessageV2 });

  const inFlightRef = useRef(new Set<string>());

  const relayMessage = useCallback(
    async (messageSid: string, target: MessageRelayTargetV2) => {
      const inFlightKey = `${messageSid}:${target.conversationId}`;

      if (inFlightRef.current.has(inFlightKey)) {
        return;
      }

      inFlightRef.current.add(inFlightKey);

      const label = getRelayTargetLabel(target);

      const properties = {
        message_sid: messageSid,
        target_conversation_id: target.conversationId,
        audience: target.audience,
      };

      try {
        const result = await mutateAsync({
          conversationId,
          messageSid,
          targetConversationId: target.conversationId,
        });

        track(ANALYTICS_EVENTS.MESSAGE_RELAYED, { ...properties, status: result.status });

        toast.showSuccess({
          title:
            result.status === 'already_relayed'
              ? `Already relayed to ${label}`
              : `Relayed to ${label}`,
        });
      } catch (error) {
        track(ANALYTICS_EVENTS.MESSAGE_RELAY_FAILED, {
          ...properties,
          error_message: error instanceof Error ? error.message : String(error),
        });

        toast.showError({ title: 'Unable to relay message', apiError: { error } });
      } finally {
        inFlightRef.current.delete(inFlightKey);
      }
    },
    [conversationId, mutateAsync, toast, track]
  );

  return { relayTargets, relayMessage };
}
